import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { danceRolesApi } from '../services/api';
import { DanceRole, CreateDanceRoleData } from '../types';

const DanceRolesManagement: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingRole, setEditingRole] = useState<DanceRole | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();

  const { data: rolesData, isLoading } = useQuery({
    queryKey: ['dance-roles', { page, search }],
    queryFn: () => danceRolesApi.getAll({ page, limit: 10, search: search || undefined }),
  });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<CreateDanceRoleData>();

  const createMutation = useMutation({
    mutationFn: danceRolesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dance-roles'] });
      reset();
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: CreateDanceRoleData }) =>
      danceRolesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dance-roles'] });
      reset();
      setEditingRole(null);
      setShowForm(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: danceRolesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dance-roles'] });
    },
  });

  const onSubmit = (data: CreateDanceRoleData) => {
    if (editingRole) {
      updateMutation.mutate({ id: editingRole.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (role: DanceRole) => {
    setEditingRole(role);
    reset({ name: role.name, description: role.description || '' });
    setShowForm(true);
  };

  const handleDelete = (role: DanceRole) => {
    if (window.confirm(`Are you sure you want to delete "${role.name}"?`)) {
      deleteMutation.mutate(role.id);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingRole(null);
    reset();
  };

  return (
    <div className="dance-roles-management">
      <div className="page-header">
        <h1>Dance Roles Management</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(true)} disabled={showForm}>
          Add New Role
        </button>
      </div>

      {showForm && (
        <div className="form-container">
          <h2>{editingRole ? 'Edit Role' : 'Create New Role'}</h2>
          <form onSubmit={handleSubmit(onSubmit)} className="admin-form">
            <div className="form-group">
              <label htmlFor="name">Name *</label>
              <input
                {...register('name', { required: 'Name is required' })}
                type="text"
                id="name"
                className={errors.name ? 'error' : ''}
              />
              {errors.name && <span className="error-text">{errors.name.message}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea {...register('description')} id="description" rows={3} />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingRole ? 'Update' : 'Create'} Role
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="table-controls">
        <input
          type="text"
          placeholder="Search roles..."
          aria-label="Search roles"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
      </div>

      {isLoading ? (
        <div className="loading">Loading roles...</div>
      ) : rolesData && rolesData.data.length > 0 ? (
        <>
          <div className="table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Breakdowns</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rolesData.data.map((role) => (
                  <tr key={role.id}>
                    <td><strong>{role.name}</strong></td>
                    <td>{role.description || '-'}</td>
                    <td>{role.breakdownsCount || 0}</td>
                    <td>{new Date(role.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn btn-small btn-secondary" onClick={() => handleEdit(role)}>Edit</button>
                        <button className="btn btn-small btn-danger" onClick={() => handleDelete(role)} disabled={deleteMutation.isPending}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rolesData.pagination.pages > 1 && (
            <div className="pagination">
              {Array.from({ length: rolesData.pagination.pages }, (_, i) => i + 1).map((pageNum) => (
                <button key={pageNum} className={`btn btn-small ${pageNum === page ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setPage(pageNum)}>
                  {pageNum}
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="empty-state">
          <p>No dance roles found. Create your first role!</p>
        </div>
      )}
    </div>
  );
};

export default DanceRolesManagement;
